import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { usageAPI } from '../utils/api';
import { useTranslation } from 'react-i18next';
import { 
  TrendingUp, Activity, Zap, ArrowUpRight, BarChart3, 
  Calendar, Clock, CheckCircle
} from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

export default function DashboardHome() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState('7days');

  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(true);
    fetchStats(timePeriod);
  }, [isAuthenticated, navigate, timePeriod]);

  const fetchStats = async (period = '7days') => {
    try {
      const [statsRes, chartsRes] = await Promise.all([
        usageAPI.getStats(),
        period === '7days' ? usageAPI.getUsageByDay() : usageAPI.getUsageByDay(30),
      ]);

      console.log('📊 Dashboard Stats Response:', statsRes.data.data);
      console.log('   - total_requests:', statsRes.data.data.total_requests);
      console.log('   - monthly_usage:', statsRes.data.data.monthly_usage);
      console.log('   - quota_remaining:', statsRes.data.data.quota_remaining);
      console.log('   - balance:', statsRes.data.data.balance);
      console.log('   - usage:', statsRes.data.data.usage);

      setStats(statsRes.data.data);
      setChartData(chartsRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
          <div className="text-[#A0AEC0] text-lg">{t('Loading dashboard...')}</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#0B0E14] relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="fixed inset-0 pointer-events-none hidden sm:block">
          <div className="absolute top-10 left-10 w-48 h-48 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-[#128C7E]/10 rounded-full blur-3xl opacity-20"></div>
          <div className="absolute -bottom-20 right-10 w-48 h-48 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-[#128C7E]/10 rounded-full blur-3xl opacity-20"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8" dir={isRTL ? 'rtl' : 'ltr'}>
          
          {/* Premium Hero Section */}
          <div className="mb-10 sm:mb-16">
            <div className="relative bg-gradient-to-br from-[#128C7E]/20 via-[#1A1F2E] to-[#0B0E14] border border-[#128C7E]/40 rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-14 overflow-hidden">
              {/* Animated Border Accent */}
              <div className="absolute inset-0 rounded-2xl sm:rounded-3xl bg-gradient-to-r from-[#128C7E]/20 via-transparent to-transparent opacity-50"></div>
              
              <div className="relative z-10">
                <div className={`flex flex-col ${isRTL ? 'md:flex-row-reverse' : 'md:flex-row'} md:items-start md:justify-between gap-4 sm:gap-8`}>
                  <div className="flex-1">
                    <div className={`inline-block mb-3 sm:mb-4 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#128C7E]/20 border border-[#128C7E] rounded-full ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className="text-[#128C7E] text-xs sm:text-sm font-semibold">{isRTL ? t('Premium Experience') + ' ✨' : '✨ ' + t('Premium Experience')}</span>
                    </div>
                    
                    <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3 sm:mb-4 leading-tight ${isRTL ? 'text-right' : 'text-left'}`}>
                      {isRTL ? '👋 ' + t('Welcome back') : t('Welcome back') + ' 👋'}
                    </h1>
                    <p className={`text-[#A0AEC0] text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 max-w-2xl ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('Monitor your API performance in real-time and optimize your integration')}
                    </p>
                    
                    <div className={`flex flex-col gap-2 sm:gap-4 items-start text-xs sm:text-sm`}>
                      <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-2 sm:gap-3 bg-[#1A1F2E] px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border border-[#2D3748]`}>
                        <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#128C7E] flex-shrink-0" />
                        <span className="text-[#A0AEC0] text-xs sm:text-sm">{new Date().toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : i18n.language, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      </div>
                      <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-2 sm:gap-3 bg-[#1A1F2E] px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border border-[#2D3748]`}>
                        <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#128C7E] flex-shrink-0" />
                        <span className="text-[#A0AEC0] text-xs sm:text-sm">{new Date().toLocaleTimeString(i18n.language === 'ar' ? 'ar-SA' : i18n.language, { hour: '2-digit', minute: '2-digit', hour12: i18n.language !== 'ar' })}</span>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* Premium KPI Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-10 sm:mb-16">
            {/* Card 1: Balance */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#10B981]/20 to-transparent rounded-2xl sm:rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative h-full bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#10B981]/30 group-hover:border-[#10B981]/60 rounded-2xl sm:rounded-3xl p-5 sm:p-8 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#10B981]/30">
                <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-start justify-between mb-5 sm:mb-8`}>
                  <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <p className="text-[#A0AEC0] text-xs font-bold uppercase tracking-wider">{t('Balance')}</p>
                  </div>
                  <div className={`w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-[#10B981]/30 to-[#10B981]/10 rounded-lg sm:rounded-2xl flex items-center justify-center group-hover:from-[#10B981]/40 group-hover:to-[#10B981]/20 transition-all flex-shrink-0 ${isRTL ? 'mr-3 sm:mr-4' : 'ml-3 sm:ml-4'}`}>
                    <CheckCircle className="w-5 h-5 sm:w-7 sm:h-7 text-[#10B981]" />
                  </div>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  <div>
                    <p
                      className={`text-3xl sm:text-5xl font-black text-white ${isRTL ? 'text-right' : 'text-left'}`}
                      style={{ fontSize: stats?.balance && stats.balance.toString().length > 7 ? '2.2rem' : undefined, wordBreak: 'break-all' }}
                    >
                      {typeof stats?.balance === 'number'
                        ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 }).format(stats.balance)
                        : t('N/A')}
                    </p>
                  </div>
                  <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-2 pt-1.5 sm:pt-2 border-t border-[#2D3748]`}>
                    <span className="text-[#10B981] text-xs font-bold">
                      {t('Available balance')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Total Requests */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#128C7E]/20 to-transparent rounded-2xl sm:rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative h-full bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#128C7E]/30 group-hover:border-[#128C7E]/60 rounded-2xl sm:rounded-3xl p-5 sm:p-8 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#128C7E]/30">
                <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-start justify-between mb-5 sm:mb-8`}>
                  <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <p className="text-[#A0AEC0] text-xs font-bold uppercase tracking-wider">{t('Total Requests')}</p>
                  </div>
                  <div className={`w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-[#128C7E]/30 to-[#128C7E]/10 rounded-lg sm:rounded-2xl flex items-center justify-center group-hover:from-[#128C7E]/40 group-hover:to-[#128C7E]/20 transition-all flex-shrink-0 ${isRTL ? 'mr-3 sm:mr-4' : 'ml-3 sm:ml-4'}`}>
                    <Activity className="w-5 h-5 sm:w-7 sm:h-7 text-[#128C7E]" />
                  </div>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  <div>
                    <p className={`text-3xl sm:text-5xl font-black text-white ${isRTL ? 'text-right' : 'text-left'}`}>
                      {stats?.total_requests ? new Intl.NumberFormat(i18n.language).format(stats.total_requests) : '0'}
                    </p>
                  </div>
                  <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-2 pt-1.5 sm:pt-2 border-t border-[#2D3748]`}>
                    <span className={`inline-flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-1 text-[#128C7E] text-xs font-bold bg-[#128C7E]/15 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full`}>
                      <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4" />
                      +24%
                    </span>
                    <span className="text-[#718096] text-xs">{t('vs last month')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Monthly Usage */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#128C7E]/20 to-transparent rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative h-full bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#128C7E]/30 group-hover:border-[#128C7E]/60 rounded-3xl p-8 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#128C7E]/30">
                <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-start justify-between mb-8`}>
                  <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <p className="text-[#A0AEC0] text-xs font-bold uppercase tracking-wider">{t('Monthly Usage')}</p>
                  </div>
                  <div className={`w-14 h-14 bg-gradient-to-br from-[#128C7E]/30 to-[#128C7E]/10 rounded-2xl flex items-center justify-center group-hover:from-[#128C7E]/40 group-hover:to-[#128C7E]/20 transition-all ${isRTL ? 'mr-4' : 'ml-4'}`}>
                    <TrendingUp className="w-7 h-7 text-[#128C7E]" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-5xl font-black text-white">
                      {stats?.monthly_usage ? new Intl.NumberFormat(i18n.language).format(stats.monthly_usage) : '0'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-[#2D3748]">
                    <span className="text-[#A0AEC0] text-xs">
                      {stats?.quota_remaining > 0 ? `${new Intl.NumberFormat(i18n.language, {notation: 'compact'}).format(stats.quota_remaining)} ${t('remaining')}` : t('Limit reached')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3: Performance */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#128C7E]/20 to-transparent rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative h-full bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#128C7E]/30 group-hover:border-[#128C7E]/60 rounded-3xl p-8 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#128C7E]/30">
                <div className="flex items-start justify-between mb-8">
                  <div className="flex-1">
                    <p className="text-[#A0AEC0] text-xs font-bold uppercase tracking-wider">{t('API Status')}</p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-[#10B981]/30 to-[#10B981]/10 rounded-2xl flex items-center justify-center">
                    <CheckCircle className="w-7 h-7 text-[#10B981]" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-2xl font-black text-white">{t('All Systems')}</p>
                    <p className="text-[#A0AEC0] text-sm mt-1">{t('Operational')}</p>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-[#2D3748]">
                    <span className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></span>
                    <span className="text-[#A0AEC0] text-xs">{t('Live')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 4: API Keys */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#128C7E]/20 to-transparent rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative h-full bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#128C7E]/30 group-hover:border-[#128C7E]/60 rounded-3xl p-8 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#128C7E]/30">
                <div className="flex items-start justify-between mb-8">
                  <div className="flex-1">
                    <p className="text-[#A0AEC0] text-xs font-bold uppercase tracking-wider">{t('Active Keys')}</p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-[#F59E0B]/30 to-[#F59E0B]/10 rounded-2xl flex items-center justify-center group-hover:from-[#F59E0B]/40 group-hover:to-[#F59E0B]/20 transition-all">
                    <Zap className="w-7 h-7 text-[#F59E0B]" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-5xl font-black text-white">{stats?.api_keys_count || '0'}</p>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-[#2D3748]">
                    <button className="text-[#128C7E] text-xs font-bold hover:text-[#0DA699] transition-colors flex items-center gap-1">
                      {t('Manage')} →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts & Analytics Section */}
          <div className="w-full mb-16">
            {/* Main Chart */}
            <div className="group">
              <div className="relative bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#2D3748] group-hover:border-[#128C7E]/40 rounded-3xl p-10 shadow-2xl transition-all duration-300 w-full">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#128C7E]/20 rounded-2xl flex items-center justify-center">
                      <BarChart3 className="w-7 h-7 text-[#128C7E]" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">{t('Performance Trend')}</h2>
                      <p className="text-[#718096] text-sm mt-0.5">{t(timePeriod === '7days' ? 'Last 7 days' : 'Last 30 days')}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 bg-[#0B0E14]/60 p-1.5 rounded-xl border border-[#2D3748]">
                    <button
                      onClick={() => setTimePeriod('7days')}
                      className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
                        timePeriod === '7days'
                          ? 'bg-[#128C7E] text-white shadow-lg shadow-[#128C7E]/40'
                          : 'text-[#A0AEC0] hover:text-white hover:bg-[#2D3748]/50'
                      }`}
                    >
                      7D
                    </button>
                    <button
                      onClick={() => setTimePeriod('30days')}
                      className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
                        timePeriod === '30days'
                          ? 'bg-[#128C7E] text-white shadow-lg shadow-[#128C7E]/40'
                          : 'text-[#A0AEC0] hover:text-white hover:bg-[#2D3748]/50'
                      }`}
                    >
                      30D
                    </button>
                  </div>
                </div>

                <div className="bg-[#0B0E14]/50 rounded-2xl p-0 w-full">
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart 
                      data={chartData}
                      margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                    >
                      <defs>
                        <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#128C7E" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#128C7E" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid 
                        strokeDasharray="3 3" 
                        stroke="#2D3748" 
                        vertical={false}
                      />
                      <XAxis 
                        dataKey="date" 
                        stroke="#718096"
                        style={{ fontSize: '12px' }}
                        tick={{ fill: '#718096' }}
                      />
                      <YAxis 
                        stroke="#718096"
                        style={{ fontSize: '12px' }}
                        tick={{ fill: '#718096' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: '#1A1F2E',
                          border: '2px solid #128C7E',
                          borderRadius: '12px',
                          boxShadow: '0 20px 60px rgba(18, 140, 126, 0.3)',
                        }}
                        cursor={{ stroke: '#128C7E', strokeWidth: 2 }}
                        labelStyle={{ color: '#FFFFFF', fontWeight: '700' }}
                        formatter={(value) => [new Intl.NumberFormat(i18n.language).format(value), t('Requests')]}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="requests" 
                        stroke="#128C7E" 
                        strokeWidth={3}
                        fill="url(#colorArea)"
                        dot={{ fill: '#128C7E', r: 4, strokeWidth: 2, stroke: '#0B0E14' }}
                        activeDot={{ r: 6 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Insights Sidebar */}
          </div>


        </div>
      </div>

      <style jsx>{`
        @keyframes rotate {
          from {
            transform: rotate(-90deg);
          }
          to {
            transform: rotate(270deg);
          }
        }
      `}</style>
    </DashboardLayout>
  );
}
